import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useContacts,
  useDeleteContact,
  useCurrentUser,
  useIsAdmin,
} from "@/hooks";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";

import Alert from "@mui/material/Alert";
import { Plus, Users, Edit, Trash2 } from "lucide-react";
import { ContactCard } from "../../components/molecules/ContactCard";
import {
  ResponsiveTable,
  type Column,
} from "../../components/molecules/ResponsiveTable/ResponsiveTable";
import { TermsModal } from "../../components/terms";
import { TERMS_VERSION } from "@/lib/terms";
import type { Contact } from "@property-reports/api-contract";

export function ContactsList() {
  const navigate = useNavigate();
  // refetch removed - was only used for CSV import which is disabled
  const { data: contacts, isLoading, error } = useContacts();
  const { data: currentUser } = useCurrentUser();
  const { isSuperAdmin } = useIsAdmin();
  const deleteContact = useDeleteContact();

  // Terms acceptance check - track if user dismissed the modal this session
  const [termsDismissed, setTermsDismissed] = useState(false);

  // Derive whether to show terms modal from user data
  const showTermsModal = useMemo(() => {
    if (termsDismissed || !currentUser || isSuperAdmin) return false;
    return (
      !currentUser.acceptedTermsAt || currentUser.termsVersion !== TERMS_VERSION
    );
  }, [currentUser, isSuperAdmin, termsDismissed]);

  const handleTermsAccepted = () => {
    setTermsDismissed(true);
  };

  const handleEdit = (id: string) => {
    navigate(`/contacts/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await deleteContact.mutateAsync({ params: { id } });
      } catch (err) {
        console.error("Failed to delete contact:", err);
      }
    }
  };

  const handleRowClick = (contact: Contact) => {
    navigate(`/contacts/${contact.id}`);
  };

  const getStatusLabel = (contact: Contact) =>
    contact.unsubscribedAt
      ? "Unsubscribed"
      : contact.isActive
        ? "Active"
        : "Inactive";

  const getStatusColor = (contact: Contact) =>
    contact.unsubscribedAt
      ? ("error" as const)
      : contact.isActive
        ? ("success" as const)
        : ("default" as const);

  const formatNextSend = (dateStr?: string) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const columns: Column<Contact>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortValue: (c) => c.name.toLowerCase(),
      render: (c) => (
        <Typography variant="body2" fontWeight={500}>
          {c.name}
        </Typography>
      ),
      minWidth: 140,
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      sortValue: (c) => c.email.toLowerCase(),
      render: (c) => (
        <Typography variant="body2" color="text.secondary">
          {c.email}
        </Typography>
      ),
      minWidth: 180,
    },
    {
      key: "suburb",
      header: "Suburb",
      sortable: true,
      sortValue: (c) => c.suburb.toLowerCase(),
      render: (c) => c.suburb,
      minWidth: 120,
    },
    {
      key: "frequency",
      header: "Frequency",
      sortable: true,
      sortValue: (c) => c.reportFrequency,
      render: (c) => (
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {c.reportFrequency}
        </Typography>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (c) => (c.unsubscribedAt ? 2 : c.isActive ? 0 : 1),
      render: (c) => (
        <Chip
          label={getStatusLabel(c)}
          color={getStatusColor(c)}
          size="small"
        />
      ),
    },
    {
      key: "nextSend",
      header: "Next Send",
      sortable: true,
      sortValue: (c) =>
        c.nextScheduledSend ? new Date(c.nextScheduledSend).getTime() : null,
      render: (c) => (
        <Typography variant="body2" color="text.secondary">
          {formatNextSend(c.nextScheduledSend)}
        </Typography>
      ),
      minWidth: 110,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (c) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(c.id);
            }}
            aria-label="Edit"
          >
            <Edit size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(c.id);
            }}
            aria-label="Delete"
            color="error"
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Render terms modal (blocks interaction until accepted)
  if (showTermsModal) {
    return (
      <TermsModal open={showTermsModal} onAccepted={handleTermsAccepted} />
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load contacts</Alert>;
  }

  const contactList = contacts ?? [];

  const tableHeader = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
      }}
    >
      <Typography variant="h4" component="h1">
        Contacts
      </Typography>
      <Button
        variant="contained"
        startIcon={<Plus size={18} />}
        onClick={() => navigate("/contacts/new")}
      >
        Add Contact
      </Button>
    </Box>
  );

  return (
    <ResponsiveTable
      data={contactList}
      columns={columns}
      getKey={(c) => c.id}
      defaultSortKey="name"
      defaultSortDirection="asc"
      onRowClick={handleRowClick}
      loading={isLoading}
      header={tableHeader}
      minTableWidth={800}
      emptyState={
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Users size={48} color="gray" style={{ margin: "0 auto" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No contacts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Get started by creating a new contact.
          </Typography>
        </Box>
      }
      mobileCardRender={(contact) => (
        <ContactCard
          contact={{
            id: contact.id,
            name: contact.name,
            email: contact.email,
            emailRecipients: contact.emailRecipients,
            suburb: contact.suburb,
            reportFrequency: contact.reportFrequency,
            isActive: contact.isActive,
            unsubscribedAt: contact.unsubscribedAt,
          }}
          onClick={(id) => navigate(`/contacts/${id}`)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    />
  );
}
